import React, { useState } from "react";
import axios from "axios";
import moment from "moment";
import "moment/locale/fr";
import { FaTimes } from "react-icons/fa";
import { API_URL } from "../constants";

moment.locale("fr");

export default function MotorErrorHistoryModal({ motor, token }) {
  const today = moment().format("YYYY-MM-DD");
  const [errorHistory, setErrorHistory] = useState([]);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // Fonction pour récupérer la liste de tous les moteurs (base + remplacements)
  const getAllMotorIds = (motor) => {
    if (!motor) return [];
    return [motor._id, ...(motor.id_moteurs_replace || [])];
  };

  const fetchErrorHistory = async (start = startDate, end = endDate) => {
    if (!motor) return;
    setMessage("");

    const startMoment = moment(start);
    let endMoment = moment(end);

    let requestEndDate = endMoment.clone(); 
    if (startMoment.isSame(endMoment, "day")) {
      requestEndDate = endMoment.clone().add(1, "day");
    }

    if (requestEndDate.diff(startMoment, "days") > 7) {
      setMessage("La période ne peut pas dépasser 7 jours.");
      return;
    }

    try {
      setLoading(true);

      const motorIds = getAllMotorIds(motor);

      // Lancer toutes les requêtes en parallèle
      const requests = motorIds.map((id) =>
        axios
          .get(
            `${API_URL}/defauts/defaut?d1=${start}&d2=${requestEndDate.format(
              "YYYY-MM-DD"
            )}&id=${id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          )
          .then((res) => res.data.data || [])
          .catch((err) => {
            console.error(`Erreur récupération défauts moteur ${id} :`, err);
            return [];
          })
      );

      const results = await Promise.all(requests);

      // Fusionner toutes les erreurs et trier par date
      const mergedErrors = results
        .flat()
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      setErrorHistory(mergedErrors);
    } catch (err) {
      console.error("Erreur lors de la récupération des défauts :", err);
      setMessage("Erreur lors de la récupération des données.");
      setTimeout(() => setMessage(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => {
    setStartDate(today);
    setEndDate(today);
    setErrorHistory([]);
    setMessage("");
    setShowModal(true);
    fetchErrorHistory(today, today);
  };

  return (
    <div>
      <button
        className="bg-bleuSTB text-white px-3 py-1.5 rounded mb-2 mt-4 text-sm sm:text-base"
        onClick={openModal}
      >
        Voir l’historique des erreurs
      </button>

      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-2 sm:p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-auto text-sm sm:text-base shadow-lg relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-2 sm:top-4 right-2 sm:right-4 text-red-500 text-xl sm:text-2xl"
              onClick={(e) => {
                e.stopPropagation();
                setShowModal(false);
              }}
            >
              <FaTimes />
            </button>

            <div className="p-4 sm:p-6 flex flex-col gap-3">
              <h2 className="text-lg sm:text-xl font-bold text-center">
                Historique des erreurs du moteur
              </h2>

              <p className="text-center text-xs sm:text-sm text-gray-600">
                Maximum 7 jours
              </p>

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 justify-center items-center flex-wrap"> 
                <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2">
                  <label className="whitespace-nowrap">Début :</label>
                  <input
                    type="date"
                    value={startDate}
                    max={endDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="border rounded p-1 sm:p-2 text-sm"
                  />
                </div>
                <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2">
                  <label className="whitespace-nowrap">Fin :</label>
                  <input
                    type="date"
                    value={endDate}
                    min={startDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="border rounded p-1 sm:p-2 text-sm"
                  />
                </div>
                <button
                  onClick={() => fetchErrorHistory(startDate, endDate)}
                  className="bg-bleuSTB text-white px-3 py-1.5 rounded mt-1 sm:mt-0 text-sm sm:text-base"
                >
                  Rechercher
                </button>
              </div>

              {message && (
                <p className="text-center text-red-500 text-sm animate-fade">
                  {message}
                </p>
              )}

              {loading ? (
                <p className="text-center text-sm sm:text-base">Chargement...</p>
              ) : errorHistory.length > 0 ? (
                <>
                  {/* Table desktop */}
                  <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full text-left border-collapse min-w-[400px] text-sm sm:text-base">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border-b p-1 sm:p-2">Date</th>
                          <th className="border-b p-1 sm:p-2">Erreur</th>
                        </tr>
                      </thead>
                      <tbody>
                        {errorHistory.map((err, idx) => (
                          <tr
                            key={err._id}
                            className={
                              idx % 2 === 0
                                ? "bg-white"
                                : "bg-gray-50 hover:bg-gray-100 transition-colors"
                            }
                          >
                            <td className="border-b p-1 sm:p-2">
                              {moment(err.timestamp).format(
                                "dddd DD MMMM YYYY HH:mm"
                              )}
                            </td>
                            <td className="border-b p-1 sm:p-2">
                              {err.defaut.join(", ")}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Cards mobile */}
                  <div className="sm:hidden flex flex-col gap-2">
                    {errorHistory.map((err) => (
                      <div
                        key={err._id}
                        className="border rounded p-2 bg-gray-50 shadow-sm"
                      >
                        <p className="font-semibold text-sm">
                          {moment(err.timestamp).format(
                            "dddd DD MMMM YYYY HH:mm"
                          )}
                        </p>
                        <p className="text-sm">{err.defaut.join(", ")}</p>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-center text-sm sm:text-base">
                  Aucune erreur sur la période sélectionnée.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
